import { Space, Button, Row, Col, Card } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import React, { useState, useRef, useEffect } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { AsyncPaginate } from 'react-select-async-paginate';
import { GroupBase, SingleValue } from 'react-select';
// Types
import {
  GetUpcomingStreams,
  GetUpcomingStreamsVariables,
  GetUpcomingStreams_adminStreams_entities,
} from 'api/streams/types/GetUpcomingStreams';
import { GetFeaturedStreams } from 'api/streams/types/GetFeaturedStreams';
import {
  SetFeaturedPpvEvents,
  SetFeaturedPpvEventsVariables,
} from 'api/streams/types/SetFeaturedPpvEvents';
import {
  StoreStatus,
  StreamStatus,
  StreamOrderBy,
  SortDirection,
} from 'api/graphql-global-types';
// Api
import {
  GET_UPCOMING_STREAMS,
  GET_FEATURED_STREAMS,
} from 'api/streams/queries';
import { SET_FEATURED_PPV_EVENTS } from 'api/streams/mutations';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
// Components
import Loader from 'uiShared/Loader/Loader';
// Styles
import styles from './FeaturedStreams.module.scss';

const PER_PAGE = 10;

// Types for selected streams with order index
interface SelectedStream {
  stream: GetUpcomingStreams_adminStreams_entities;
  orderIndex: number;
}

// Draggable Stream Row Component using native HTML5 drag and drop
interface DraggableStreamRowProps {
  selectedStream: SelectedStream;
  index: number;
  moveStream: (dragIndex: number, hoverIndex: number) => void;
  removeStream: (index: number) => void;
}

const DraggableStreamRow: React.FC<DraggableStreamRowProps> = ({
  selectedStream,
  index,
  moveStream,
  removeStream,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleDragStart = (e: DragEvent) => {
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      const dragIndex = parseInt(e.dataTransfer?.getData('text/plain') || '0');
      if (dragIndex !== index) {
        moveStream(dragIndex, index);
      }
    };

    element.setAttribute('draggable', 'true');
    element.addEventListener('dragstart', handleDragStart);
    element.addEventListener('dragover', handleDragOver);
    element.addEventListener('drop', handleDrop);

    return () => {
      element.removeEventListener('dragstart', handleDragStart);
      element.removeEventListener('dragover', handleDragOver);
      element.removeEventListener('drop', handleDrop);
    };
  }, [index, moveStream]);

  return (
    <div ref={ref} className={styles.row}>
      <div className={styles.grip} aria-hidden="true">
        <HolderOutlined />
      </div>

      <div className={styles.rank}>{selectedStream.orderIndex}</div>

      <div className={styles.details}>
        <div className={styles.name}>
          {selectedStream.stream.name || 'Unnamed Stream'}
        </div>
        <div className={styles.store}>
          {selectedStream.stream.store.storeDetails?.storeName ||
            `${selectedStream.stream.store.storeDetails?.storeName} ${selectedStream.stream.store.lastName}`}
        </div>
      </div>

      <Button
        type="text"
        danger
        onClick={() => removeStream(index)}
        size="small"
      >
        Remove
      </Button>
    </div>
  );
};

const FeaturedStreams: React.FC = () => {
  const [selectedStreams, setSelectedStreams] = useState<SelectedStream[]>([]);
  const [selectedStreamOption, setSelectedStreamOption] =
    useState<GetUpcomingStreams_adminStreams_entities | null>(null);

  const [getUpcomingStreams, { data: upcomingStreamsData }] = useLazyQuery<
    GetUpcomingStreams,
    GetUpcomingStreamsVariables
  >(GET_UPCOMING_STREAMS);

  const { data: featuredStreamsData, loading: featuredStreamsLoading } =
    useQuery<GetFeaturedStreams>(GET_FEATURED_STREAMS, {
      fetchPolicy: 'network-only',
    });

  // Load upcoming streams on component mount
  useEffect(() => {
    getUpcomingStreams({
      variables: {
        input: {
          streamStatus: [StreamStatus.Scheduled],
          orderBy: StreamOrderBy.Name,
          direction: SortDirection.ASC,
          storeStatus: StoreStatus.Active,
          limit: PER_PAGE,
          offset: 0,
        },
      },
    });
  }, [getUpcomingStreams]);

  useEffect(() => {
    if (featuredStreamsData) {
      setSelectedStreams(
        featuredStreamsData.featuredPpvEvents.map((event) => ({
          stream:
            event.stream as unknown as GetUpcomingStreams_adminStreams_entities,
          orderIndex: event.orderIndex,
        }))
      );
    }
  }, [featuredStreamsData]);

  const [setFeaturedPpvEvents, { loading: setFeaturedPpvEventsLoading }] =
    useMutation<SetFeaturedPpvEvents, SetFeaturedPpvEventsVariables>(
      SET_FEATURED_PPV_EVENTS
    );

  const loadMore = async (offset: number, search?: string) => {
    const options: GetUpcomingStreams_adminStreams_entities[] = [];

    await getUpcomingStreams({
      variables: {
        input: {
          ...(search
            ? {
                storeName: search,
              }
            : {}),
          streamStatus: [StreamStatus.Scheduled],
          orderBy: StreamOrderBy.StoreName,
          direction: SortDirection.DESC,
          storeStatus: StoreStatus.Active,
          limit: PER_PAGE,
          offset,
        },
      },
    });

    const total = upcomingStreamsData?.adminStreams?.total || 0;

    const unselectedStreams =
      upcomingStreamsData?.adminStreams?.entities?.filter(
        (stream) =>
          !selectedStreams.some((item) => item.stream.id === stream.id)
      ) || [];

    unselectedStreams.forEach((item) => {
      options.push(item);
    });

    return { total, options };
  };

  const moveStream = (dragIndex: number, hoverIndex: number) => {
    console.log(`Moving stream from ${dragIndex} to ${hoverIndex}`);
    const newSelectedStreams = [...selectedStreams];
    const draggedItem = newSelectedStreams[dragIndex];

    // Remove the dragged item
    newSelectedStreams.splice(dragIndex, 1);

    // Insert it at the new position
    newSelectedStreams.splice(hoverIndex, 0, draggedItem);

    // Update order indices
    newSelectedStreams.forEach((item, index) => {
      item.orderIndex = index + 1;
    });

    setSelectedStreams(newSelectedStreams);
  };

  const removeStream = (index: number) => {
    const newSelectedStreams = selectedStreams.filter((_, i) => i !== index);

    // Update order indices
    newSelectedStreams.forEach((item, index) => {
      item.orderIndex = index + 1;
    });

    setSelectedStreams(newSelectedStreams);
  };

  const addStream = () => {
    if (selectedStreamOption) {
      const newOrderIndex = selectedStreams.length + 1;
      const newSelectedStream: SelectedStream = {
        stream: selectedStreamOption,
        orderIndex: newOrderIndex,
      };

      setSelectedStreams([...selectedStreams, newSelectedStream]);
      setSelectedStreamOption(null);
    }
  };

  const loadOptions = async (search: string, prevOptions: any) => {
    let hasMore = false;
    const offset = Array.isArray(prevOptions) ? prevOptions.length : 0;
    let options: GetUpcomingStreams_adminStreams_entities[] = [];

    await loadMore(offset, search)
      .then(({ total, options: newOptions }) => {
        options = newOptions;
        hasMore = offset < total && !(total < PER_PAGE);
      })
      .catch(() => {
        hasMore = false;
      });

    return { options, hasMore };
  };

  const onStreamSelect = (
    option: SingleValue<GetUpcomingStreams_adminStreams_entities>
  ) => {
    setSelectedStreamOption(option);
  };

  const handleSaveChanges = async () => {
    try {
      await setFeaturedPpvEvents({
        variables: {
          input: selectedStreams.map((item) => ({
            streamId: item.stream.id,
            orderIndex: item.orderIndex,
          })),
        },
      });
      successNotification('Featured streams updated successfully');
    } catch (error) {
      errorNotification('Failed to update featured streams');
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card title="Selected Featured Streams" size="small">
        {featuredStreamsLoading ? (
          <div className={styles.empty}>
            <Loader loading={true} size="large" />
          </div>
        ) : selectedStreams.length === 0 ? (
          <div className={styles.empty}>
            No streams selected yet. Use the selector below to add streams.
          </div>
        ) : (
          <div>
            <div className={styles.list}>
              {selectedStreams.map((selectedStream, index) => (
                <DraggableStreamRow
                  key={`${selectedStream.stream.id}-${selectedStream.orderIndex}`}
                  selectedStream={selectedStream}
                  index={index}
                  moveStream={moveStream}
                  removeStream={removeStream}
                />
              ))}
            </div>
            <div className={styles.footer}>
              <Button
                type="primary"
                onClick={handleSaveChanges}
                loading={setFeaturedPpvEventsLoading}
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card title="Add New Stream" size="small">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <AsyncPaginate<
              GetUpcomingStreams_adminStreams_entities,
              GroupBase<GetUpcomingStreams_adminStreams_entities>,
              any
            >
              value={selectedStreamOption}
              isDisabled={selectedStreams.length >= 5}
              isClearable
              placeholder="Search and select a stream..."
              onChange={onStreamSelect}
              loadOptions={loadOptions}
              onMenuOpen={() => {
                // Ensure options are loaded when dropdown opens
                if (!upcomingStreamsData?.adminStreams?.entities?.length) {
                  getUpcomingStreams({
                    variables: {
                      input: {
                        streamStatus: [StreamStatus.Scheduled],
                        orderBy: StreamOrderBy.Name,
                        direction: SortDirection.ASC,
                        storeStatus: StoreStatus.Active,
                        limit: PER_PAGE,
                        offset: 0,
                      },
                    },
                  });
                }
              }}
              getOptionLabel={(option) =>
                `${option.name || 'Unnamed Stream'} - ${
                  option.store.storeDetails?.storeName ||
                  `${option.store.firstName} ${option.store.lastName}`
                }`
              }
              getOptionValue={(option) => option.id}
              styles={{
                // Match the Ant Design inputs the rest of the workspace uses.
                control: (provided, state) => ({
                  ...provided,
                  minHeight: 38,
                  borderRadius: 7,
                  borderColor: state.isFocused ? '#84a8fa' : '#d0d7e2',
                  boxShadow: state.isFocused
                    ? '0 0 0 3px rgba(37, 99, 235, 0.1)'
                    : '0 1px 2px rgba(16, 24, 40, 0.02)',
                  '&:hover': {
                    borderColor: state.isFocused ? '#84a8fa' : '#b3c2d8',
                  },
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: '#8691a3',
                }),
                menu: (provided) => ({
                  ...provided,
                  borderRadius: 9,
                  overflow: 'hidden',
                  boxShadow: '0 8px 28px rgba(16, 24, 40, 0.12)',
                }),
              }}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={addStream}
              disabled={!selectedStreamOption || selectedStreams.length >= 5}
            >
              Add
            </Button>
          </Col>
        </Row>
      </Card>
    </Space>
  );
};

export default FeaturedStreams;
